// The small icon set the context menu uses.
//
// Inline SVG rather than glyphs: an emoji or a box-drawing character renders at
// a different size, weight and baseline on every platform, and a menu is where
// that shows worst — a column of icons that do not line up reads as broken.
// These are all one 16px box, 1.5px stroke, same visual weight.

interface IconProps {
  /** Rendered at the text's own colour, so an item's state colours its icon. */
  className?: string;
}

function Svg({ children, className }: IconProps & { children: React.ReactNode }): JSX.Element {
  return (
    <svg
      className={`gt-icon${className ? ` ${className}` : ''}`}
      viewBox="0 0 16 16"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export const Icon = {
  cut: (p: IconProps) => (
    <Svg {...p}>
      <circle cx="4" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <path d="M5.5 10.5 12 2M10.5 10.5 4 2" />
    </Svg>
  ),
  copy: (p: IconProps) => (
    <Svg {...p}>
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
      <path d="M10.5 3.5A1.5 1.5 0 0 0 9 2H4a1.5 1.5 0 0 0-1.5 1.5v5A1.5 1.5 0 0 0 4 10" />
    </Svg>
  ),
  paste: (p: IconProps) => (
    <Svg {...p}>
      <rect x="3.5" y="3" width="9" height="11" rx="1.5" />
      <path d="M6 3V2.5A1 1 0 0 1 7 1.5h2a1 1 0 0 1 1 1V3" />
    </Svg>
  ),
  plus: (p: IconProps) => (
    <Svg {...p}>
      <path d="M8 3.5v9M3.5 8h9" />
    </Svg>
  ),
  trash: (p: IconProps) => (
    <Svg {...p}>
      <path d="M3 4.5h10M6.5 4.5V3a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1.5M4.5 4.5l.6 8a1 1 0 0 0 1 .9h3.8a1 1 0 0 0 1-.9l.6-8" />
    </Svg>
  ),
  eraser: (p: IconProps) => (
    <Svg {...p}>
      <path d="M3 11 8.5 5.5a1.5 1.5 0 0 1 2.1 0l2 2a1.5 1.5 0 0 1 0 2.1L9.5 13H5.5Z" />
      <path d="M13 13.5H6" />
    </Svg>
  ),
  hide: (p: IconProps) => (
    <Svg {...p}>
      <path d="M2 8s2.4-4 6-4 6 4 6 4-2.4 4-6 4-6-4-6-4Z" />
      <path d="M2 2l12 12" />
    </Svg>
  ),
  show: (p: IconProps) => (
    <Svg {...p}>
      <path d="M2 8s2.4-4 6-4 6 4 6 4-2.4 4-6 4-6-4-6-4Z" />
      <circle cx="8" cy="8" r="1.6" />
    </Svg>
  ),
  resize: (p: IconProps) => (
    <Svg {...p}>
      <path d="M8 2.5v11M5.5 5 8 2.5 10.5 5M5.5 11 8 13.5 10.5 11" />
    </Svg>
  ),
  move: (p: IconProps) => (
    <Svg {...p}>
      <path d="M8 2v12M2 8h12M6 4 8 2l2 2M6 12l2 2 2-2M4 6 2 8l2 2M12 6l2 2-2 2" />
    </Svg>
  ),
  text: (p: IconProps) => (
    <Svg {...p}>
      <path d="M3 4V3h10v1M8 3v10M6 13h4" />
    </Svg>
  ),
  fill: (p: IconProps) => (
    <Svg {...p}>
      <path d="M6 2 3 5l5 5 5-5-5-5Z" />
      <path d="M13 10.5c0 1-1 2-2 2" />
    </Svg>
  ),
  borders: (p: IconProps) => (
    <Svg {...p}>
      <rect x="2.5" y="2.5" width="11" height="11" rx="1" />
      <path d="M8 2.5v11M2.5 8h11" strokeDasharray="1.5 1.5" />
    </Svg>
  ),
  /** Two arrows closing toward the middle — cells becoming one. */
  merge: (p: IconProps) => (
    <Svg {...p}>
      <rect x="2.5" y="2.5" width="11" height="11" rx="1" />
      <path d="M5 5.5 7 8 5 10.5M11 5.5 9 8 11 10.5" />
    </Svg>
  ),
  /** The divider back, splitting the one cell into two again. */
  unmerge: (p: IconProps) => (
    <Svg {...p}>
      <rect x="2.5" y="2.5" width="11" height="11" rx="1" />
      <path d="M8 2.5v11" />
    </Svg>
  ),
  number: (p: IconProps) => (
    <Svg {...p}>
      <path d="M6 2.5 5 13.5M11 2.5l-1 11M3 5.5h10M2.5 10.5h10" />
    </Svg>
  ),
  brush: (p: IconProps) => (
    <Svg {...p}>
      <path d="M4 10c-1 1-1 3-2 3.5 1.5.5 3.5.5 4.5-1a1.8 1.8 0 0 0-2.5-2.5Z" />
      <path d="M6.5 9.5 12.5 3a1.4 1.4 0 0 1 2 2L8 11" />
    </Svg>
  ),
  freeze: (p: IconProps) => (
    <Svg {...p}>
      <rect x="2.5" y="2.5" width="11" height="11" rx="1" />
      <path d="M2.5 6.5h11M6.5 2.5v11" />
    </Svg>
  ),
  filter: (p: IconProps) => (
    <Svg {...p}>
      <path d="M2.5 3.5h11l-4.2 5v4l-2.6 1.5v-5.5Z" />
    </Svg>
  ),
  /** A cylinder — the database. */
  database: (p: IconProps) => (
    <Svg {...p}>
      <ellipse cx="8" cy="3.8" rx="5" ry="1.8" />
      <path d="M3 3.8v8.4c0 1 2.2 1.8 5 1.8s5-.8 5-1.8V3.8" />
      <path d="M3 8c0 1 2.2 1.8 5 1.8S13 9 13 8" />
    </Svg>
  ),
  /** A table with a header row — the schedule. */
  schedule: (p: IconProps) => (
    <Svg {...p}>
      <rect x="2.5" y="2.5" width="11" height="11" rx="1" />
      <path d="M2.5 6h11M2.5 9.5h11M6.5 6v7.5" />
    </Svg>
  ),
  chip: (p: IconProps) => (
    <Svg {...p}>
      <rect x="2" y="5" width="12" height="6" rx="3" />
      <path d="M9.5 6.8 8 8.4 6.5 6.8" />
    </Svg>
  ),
  sortAsc: (p: IconProps) => (
    <Svg {...p}>
      <path d="M4.5 12.5V3.5M2 6l2.5-2.5L7 6M9.5 4.5h5M9.5 8h4M9.5 11.5h3" />
    </Svg>
  ),
  sortDesc: (p: IconProps) => (
    <Svg {...p}>
      <path d="M4.5 3.5v9M2 10l2.5 2.5L7 10M9.5 4.5h3M9.5 8h4M9.5 11.5h5" />
    </Svg>
  ),
  /** Three bars flush left, shortest last — the Excel align-left glyph. */
  alignLeft: (p: IconProps) => (
    <Svg {...p}>
      <path d="M3 4.5h10M3 8h7M3 11.5h4" />
    </Svg>
  ),
  /** Three bars centred, shortest last — the Excel align-centre glyph. */
  alignCenter: (p: IconProps) => (
    <Svg {...p}>
      <path d="M3 4.5h10M4.5 8h7M6 11.5h4" />
    </Svg>
  ),
  /** Three bars flush right, shortest last — the Excel align-right glyph. */
  alignRight: (p: IconProps) => (
    <Svg {...p}>
      <path d="M3 4.5h10M6 8h7M9 11.5h4" />
    </Svg>
  ),
  chevron: (p: IconProps) => (
    <Svg {...p}>
      <path d="M6 3.5 10.5 8 6 12.5" />
    </Svg>
  ),
  bold: (p: IconProps) => (
    <Svg {...p}>
      <path d="M4.5 3h4a2.5 2.5 0 0 1 0 5h-4Zm0 5h4.5a2.5 2.5 0 0 1 0 5H4.5Z" />
    </Svg>
  ),
  italic: (p: IconProps) => (
    <Svg {...p}>
      <path d="M10.5 3h-3M8.5 13h-3M9.5 3l-3 10" />
    </Svg>
  ),
  /** A magnifier with a plus: move the map onto this. */
  zoom: (p: IconProps) => (
    <Svg {...p}>
      <circle cx="7" cy="7" r="4" />
      <path d="M10 10l3.5 3.5M5.2 7h3.6M7 5.2v3.6" />
    </Svg>
  ),
  /** The map cursor: pick this one out of everything else. */
  select: (p: IconProps) => (
    <Svg {...p}>
      <path d="M3 2.5l4.2 11 1.6-4.6 4.7-1.6Z" />
    </Svg>
  ),
  check: (p: IconProps) => (
    <Svg {...p}>
      <path d="M3.5 8.5 6.5 11.5 12.5 4.5" />
    </Svg>
  ),
};

export type IconName = keyof typeof Icon;
